import React, { useEffect } from "react";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";

const CreateNotificationModal = ({
  showModal,
  setShowModal,
  formData,
  setFormData,
  patients,
  isSubmitting,
  handleSubmit,
  handleInputChange,
  handlePatientSelection,
  resetForm,
}) => {
  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  // ✅ Force type to PUSH whenever modal opens
  useEffect(() => {
    if (showModal) {
      setFormData((prev) => ({ ...prev, type: "push" }));
    }
  }, [showModal, setFormData]);

  const handleAudienceSelect = (audience) => {
    setFormData((prev) => ({ ...prev, audience }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-lg shadow-xl overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Create New Notification
            </h3>
            <button
              onClick={handleCloseModal}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto h-[calc(100vh-120px)] sm:max-h-[60vh]"
        >
          <div className="p-4 sm:p-6 space-y-4">
            {/* Notification Type (Push Only) */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Notification Type *
              </label>

              <div className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg border-blue-500 bg-blue-50 text-blue-700">
                <Icon name="Smartphone" size={16} />
                <span className="text-xs font-medium">Push Notification</span>
              </div>

              {/* keep hidden input for form submission compatibility */}
              <input type="hidden" name="type" value="push" />
            </div>

            {/* Title and Message */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  maxLength={65}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter notification title (max 65 characters)"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  maxLength={240}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter push notification message (max 240 characters)"
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length} / 240 characters
                </div>
              </div>
            </div>

            {/* Audience Selection */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Target Audience *
              </label>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {[
                  { value: "all", label: "All", fullLabel: "All Patients", icon: "Users" },
                  {
                    value: "selected_patients",
                    label: "Select",
                    fullLabel: "Select Patients",
                    icon: "UserCheck",
                  },
                ].map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    onClick={() => handleAudienceSelect(option.value)}
                    className={`p-2 sm:p-3 border rounded-lg text-center transition-colors ${
                      formData.audience === option.value
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Icon name={option.icon} size={14} className="sm:text-lg mx-auto mb-1" />
                    <span className="text-xs font-medium">
                      <span className="sm:hidden">{option.label}</span>
                      <span className="hidden sm:inline">{option.fullLabel}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Patient Selection (if selected_patients) */}
            {formData.audience === "selected_patients" && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Select Patients *
                </label>
                <div className="border border-gray-300 rounded-md p-3 max-h-32 sm:max-h-40 overflow-y-auto">
                  {patients.length === 0 ? (
                    <p className="text-xs text-gray-500 text-center">Loading patients...</p>
                  ) : (
                    patients.map((patient) => (
                      <label key={patient.id} className="flex items-center space-x-2 py-1">
                        <input
                          type="checkbox"
                          checked={formData.selectedPatients.includes(patient.id)}
                          onChange={() => handlePatientSelection(patient.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-xs text-gray-700 truncate">
                          {patient.fullName} - {patient.contactNo}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formData.selectedPatients.length} patients selected
                </div>
              </div>
            )}

            {/* Scheduling */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="schedule"
                checked={formData.schedule}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
              />
              <label className="text-xs font-medium text-gray-700">Schedule for later</label>
            </div>

            {formData.schedule && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Schedule Date & Time *
                </label>
                <input
                  type="datetime-local"
                  name="scheduledAt"
                  value={formData.scheduledAt}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {/* Additional Options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Image URL (Optional)
                </label>
                <input
                  type="url"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-2 sm:space-x-3 sticky bottom-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCloseModal}
              className="text-xs sm:text-sm px-3 py-2"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="solid"
              size="sm"
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm px-3 py-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-1 sm:mr-2"></div>
                  <span className="sm:inline">Creating...</span>
                  <span className="sm:hidden">Saving...</span>
                </>
              ) : formData.schedule ? (
                <>
                  <span className="sm:inline">Schedule Notification</span>
                  <span className="sm:hidden">Schedule</span>
                </>
              ) : (
                <>
                  <span className="sm:inline">Send Notification</span>
                  <span className="sm:hidden">Send</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateNotificationModal;
